import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion, useScroll, useTransform } from 'framer-motion';

const imageDef: SlotGroupDefinition = {
  label: 'Center Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Prime living amenity',
    },
  },
};

/** Half of the starting image width + gap, so words clear the image edges. */
const WORD_INSET = 'calc(50% + 16vw + clamp(1.5rem, 3vw, 3rem))';

export function HomePrimeLiving() {
  const image = useSlotGroup('homeprimeliving.image', imageDef);
  const sectionRef = useRef<HTMLElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsMobile(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  // Mobile offsets must match the sticky band (top-[25vh] h-[50vh]) so progress spans exactly the pinned range.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: isMobile ? ['start 25%', 'end 75%'] : ['start start', 'end start'],
  });

  const leftX = useTransform(scrollYProgress, [0, 0.7], ['0%', '-140%']);
  const rightX = useTransform(scrollYProgress, [0, 0.7], ['0%', '140%']);
  const wordOpacity = useTransform(scrollYProgress, [0, 0.25, 0.55], [1, 0.85, 0]);
  const imageWidth = useTransform(scrollYProgress, [0, 0.7], ['32vw', '100vw']);
  const imageRadius = useTransform(scrollYProgress, [0, 0.7], [4, 0]);

  return (
    <section ref={sectionRef} className="relative min-h-[150vh] w-full bg-[rgb(var(--ala-surface))] md:min-h-[220vh]">
      <div className="sticky top-[25vh] flex h-[50vh] w-full md:top-0 md:h-screen items-center justify-center overflow-hidden">
        <div className="relative flex h-full w-full items-center justify-center">
          <motion.h2
            style={{ x: leftX, opacity: wordOpacity, right: WORD_INSET }}
            className="absolute z-20 whitespace-nowrap font-accent text-[clamp(40px,7.39vw,120px)] font-bold leading-[0.8] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          >
            <RichTextContent path="homeprimeliving.left" label="Left Title" as="span" />
          </motion.h2>

          <EditableSlotGroup groupPath="homeprimeliving.image" definition={imageDef}>
            <motion.div
              style={{ width: imageWidth, borderRadius: imageRadius }}
              className="relative z-10 shrink-0 overflow-hidden will-change-[width]"
            >
              <VibesitesImage
                url={
                  image.image ||
                  ''
                }
                alt={image.alt || ''}
                sizes="(min-width: 768px) 100vw, 90vw"
                className="h-[50vh] w-full object-cover opacity-80 md:h-[80vh] md:opacity-100"
              />
            </motion.div>
          </EditableSlotGroup>

          <motion.h2
            style={{ x: rightX, opacity: wordOpacity, left: WORD_INSET }}
            className="absolute z-20 whitespace-nowrap font-accent text-[clamp(40px,7.39vw,120px)] font-bold leading-[0.8] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          >
            <RichTextContent path="homeprimeliving.right" label="Right Title" as="span" />
          </motion.h2>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomePrimeLiving',
  displayName: 'Home Prime Living',
  tokens: {
    'homeprimeliving.left': {
      label: 'Left Title',
      type: 'richtext',
      defaultValue: '<p>Prime</p>',
    },
    'homeprimeliving.right': {
      label: 'Right Title',
      type: 'richtext',
      defaultValue: '<p>Living</p>',
    },
  },
  slotGroups: {
    'homeprimeliving.image': imageDef,
  },
};

(HomePrimeLiving as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default HomePrimeLiving;
